import Link from 'next/link'
import React, { type AnchorHTMLAttributes, type ButtonHTMLAttributes } from 'react'

import { cn } from './cn'

type ButtonVariant = 'primary' | 'default' | 'ghost' | 'brand' | 'white'
type ButtonSize = 'small' | 'default' | 'large' | 'xl'

interface ButtonBaseProps {
  className?: string
  children?: React.ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
}

type ButtonLinkProps = ButtonBaseProps &
  Omit<React.ComponentProps<typeof Link>, keyof ButtonBaseProps | 'href'> & {
    to: string
    href?: never
  }

type ButtonAnchorProps = ButtonBaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps | 'href'> & {
    href: string
    to?: never
  }

type ButtonNativeProps = ButtonBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> & {
    href?: never
    to?: never
  }

type ButtonProps = ButtonLinkProps | ButtonAnchorProps | ButtonNativeProps

// Routes no longer served by this app's client router for Ship&Play — a soft
// navigation into them renders the deprecated in-repo page (or its raw RSC
// payload, GSE-11866). Render a plain anchor so the server routes the request.
const HARD_NAV_ROUTES = ['/book/ship']

function getButtonClassName({
  className,
  variant = 'default',
  size = 'default',
}: Pick<ButtonBaseProps, 'className' | 'variant' | 'size'>): string {
  return cn(
    // `w-fit` by default so buttons hug their label; callers opt into full
    // width with `snplay:w-full` (twMerge in `cn` lets it override this).
    'snplay-can-focus snplay:flex snplay:w-fit snplay:cursor-pointer snplay:items-center',
    'snplay:justify-center snplay:gap-2 snplay:whitespace-nowrap',
    'snplay:rounded-lg snplay:border snplay:border-transparent',
    'snplay:font-semibold snplay:transition',
    'snplay:hover:bg-primaryForeground snplay:hover:text-primary snplay:hover:shadow-md',
    'snplay:disabled:pointer-events-none snplay:disabled:opacity-40',
    'snplay:disabled:shadow-none',
    variant === 'brand' && 'snplay:bg-primary snplay:text-primaryForeground',
    variant === 'primary' && 'snplay:bg-ui-primary snplay:text-ui-foreground',
    variant === 'default' && 'snplay:bg-ui-decoratorLight snplay:text-ui-primary',
    variant === 'ghost' && 'snplay:bg-transparent snplay:text-ui-primary',
    variant === 'white' &&
      'snplay:bg-background snplay:text-ui-primary' +
        ' snplay:hover:bg-primary snplay:hover:text-ui-primary',
    size === 'small' && 'snplay:min-h-9 snplay:px-3 snplay:text-xs',
    size === 'default' && 'snplay:min-h-12 snplay:px-4 snplay:text-sm snplay:xs:min-h-11',
    size === 'large' && 'snplay:min-h-16 snplay:px-6 snplay:text-base snplay:xs:min-h-14',
    size === 'xl' && 'snplay:min-h-18 snplay:px-6 snplay:text-base snplay:xs:min-h-16',
    className,
  )
}

type PresentationalKey = 'className' | 'children' | 'variant' | 'size'
const PRESENTATIONAL_KEYS: readonly PresentationalKey[] = [
  'className',
  'children',
  'variant',
  'size',
]

function getRestProps<T extends ButtonBaseProps>(
  props: T,
  extraKeys: readonly string[] = [],
): Omit<T, PresentationalKey> {
  const skip = new Set<string>([...PRESENTATIONAL_KEYS, ...extraKeys])
  const rest: Record<string, unknown> = {}
  for (const key of Object.keys(props)) {
    if (!skip.has(key)) {
      rest[key] = (props as Record<string, unknown>)[key]
    }
  }
  return rest as Omit<T, PresentationalKey>
}

export const Button = (props: ButtonProps): React.ReactElement => {
  const { className, children, variant = 'default', size = 'default' } = props
  const buttonClassName = getButtonClassName({ className, variant, size })

  if ('to' in props && props.to) {
    const rest = getRestProps(props, ['to']) as Omit<ButtonLinkProps, PresentationalKey | 'to'>
    if (HARD_NAV_ROUTES.some((route) => props.to.startsWith(route))) {
      return (
        <a href={props.to} className={buttonClassName} {...rest}>
          {children}
        </a>
      )
    }
    return (
      <Link href={props.to} className={buttonClassName} {...rest}>
        {children}
      </Link>
    )
  }

  if ('href' in props && props.href) {
    const rest = getRestProps(props) as Omit<ButtonAnchorProps, PresentationalKey>
    const { href } = props

    // Same-page hash links (e.g. "#lead-form") should glide to the target
    // rather than the browser's instant jump. Progressive enhancement: `href`
    // stays on the anchor so JS-off, middle-click, and "open in new tab" still
    // work, and the native jump is the fallback. scrollIntoView honors the
    // target's `scroll-mt-*` offset just like native hash navigation.
    const handleHashScroll: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
      rest.onClick?.(event)

      if (
        !href.startsWith('#') ||
        href.length < 2 ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return
      }

      const target = document.getElementById(href.slice(1))
      if (!target) return

      event.preventDefault()
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' })
      // Keep the URL hash in sync so the link stays shareable and Back works.
      window.history.pushState(null, '', href)
    }

    return (
      <a className={buttonClassName} {...rest} onClick={handleHashScroll}>
        {children}
      </a>
    )
  }

  const nativeProps = props as ButtonNativeProps
  const { type } = nativeProps
  const rest = getRestProps(nativeProps, ['type']) as Omit<
    ButtonNativeProps,
    PresentationalKey | 'type'
  >
  return (
    <button type={type ?? 'button'} className={buttonClassName} {...rest}>
      {children}
    </button>
  )
}
